/**
 * Review Page - Final review before transaction
 *
 * Screen showing all collected information for final review before submission
 * Part of the sequential flow: Calculator → Phone → Address → ID → Review → Status
 */

import { t } from "@lingui/core/macro";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Eye, EyeOff, TriangleAlert, X } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import type { QuoteResponse, Sep24TransactionResponse } from "@/api";
import { FormReminder } from "@/components/forms/FormReminder";
import { FixedBottomBar } from "@/components/layout/FixedBottomBar";
import { RampsButton } from "@/components/ramps/Button";
import { EditableField } from "@/components/ramps/EditableField";
import { Locator } from "@/components/ramps/Locator";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { getIdLabel } from "@/constants/get-id-labels";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { FEATURE_FLAGS, useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useAnalytics } from "@/hooks/usePostHog";
import { sendLegacySep24CloseNotification } from "@/lib/legacy-sep24-postmessage";
import {
	isEmbedded,
	useCommitInterception,
} from "@/lib/parent-message-handler";
import { getAuthTokenFromSearchParams } from "@/lib/ramps-search-schema";
import { trackError } from "@/utils/analytics-helpers";
import { captureException } from "@/utils/error-tracking";
import { RampsText } from "../components/ramps/Text";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerTitle,
} from "../components/ui/drawer";
import { Progress } from "../components/ui/progress";
import { isAmountEditable, isBankService } from "../domain/calculator";
import { useCountries } from "../hooks/useCountries";
import { navigateWithRampsState } from "../lib/ramps-navigation";
import { validateRampsSearch } from "../lib/ramps-search-schema";
import { toast } from "../lib/toast";
import { useWebViewIntegration } from "../lib/url-state";
import {
	capitalizeFirstLetter,
	formatNumber,
	formatStateForDisplay,
} from "../lib/utils";
import { rampsStore } from "../stores/ramps-store";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";
import type {
	EmbeddedWalletEventProperties,
	FormAnalyticsProperties,
	TransactionAnalyticsProperties,
} from "../utils/analytics";
import { ANALYTICS_EVENTS } from "../utils/analytics";
import { buildDepositQuoteRequest } from "../utils/buildDepositQuoteRequest";
import { resolveCashInTaxes } from "../utils/cash-in-taxes";
import { buildSendCommitPayload } from "../utils/transactionPayloadBuilder";

export const Route = createFileRoute("/ramps/review")({
	validateSearch: (search: Record<string, unknown>) =>
		validateRampsSearch(search),
	component: ReviewPage,
});

function ReviewPage() {
	const navigate = useNavigate();
	const store = useRampsStore();
	const search = Route.useSearch();
	const analytics = useAnalytics();
	const { trackPageView, trackEvent } = analytics;
	const { isFeatureEnabled } = useFeatureFlags();
	const [showTermsDrawer, setShowTermsDrawer] = useState(false);
	const [locationSheetOpen, setLocationSheetOpen] = useState(false);
	const [isLocationUpdating, setIsLocationUpdating] = useState(false);
	const [showIdNumber, setShowIdNumber] = useState(false);
	const [showBankCode, setShowBankCode] = useState(false);
	const [showAccountNumber, setShowAccountNumber] = useState(false);
	const [isWaitingForParent, setIsWaitingForParent] = useState(false);

	// Commit interception for embedded mode (iframe/WebView)
	const { sendToWebView } = useWebViewIntegration();
	const { waitForCommitConfirmation, cancelWait } = useCommitInterception();

	// Track page view
	useEffect(() => {
		trackPageView("review", { page_name: "review" });
	}, [trackPageView]);

	// Get countries for name mapping
	const { getCountryLabel } = useCountries();

	// Token and transaction_id are synced globally by URLParamsInitializer
	// No need to sync here

	// Initialize API client with token fallback logic (supports both v2 and v5 API)
	const authToken = getAuthTokenFromSearchParams(search) || store.token;
	const { apiClient } = useApiClientWithAutoVersion({
		authToken,
	});

	// Mutation for saving bank account to profile
	const saveBankAccountMutation = useMutation({
		mutationKey: ["saveBankAccount"],
		mutationFn: async (params: {
			digitalProfileId: string;
			saveRequest: import("@/api").SaveBankAccountRequest;
		}) => {
			return await apiClient.profile.saveBankAccount(
				params.digitalProfileId,
				params.saveRequest,
			);
		},
		onSuccess: () => {
			// Note: Don't update store here - user should continue to see the bank details
			// they entered (from bankFieldsGFFP) on the review page, not the masked account
		},
		onError: () => {
			// Don't fail the validation - just log the error
		},
	});

	// Validation is now handled in the identification page
	// Review page only displays data that has already been validated

	// Final commit query - Step 2: final transaction commit
	const commitQuery = useQuery({
		queryKey: ["commitTransaction", store.transactionId, authToken],
		queryFn: () => {
			if (!store.transactionId || !authToken) {
				throw new Error("❌ Missing transaction ID or token for commit");
			}

			return apiClient.sendCommit.commitTransaction(store.transactionId);
		},
		enabled: false, // Don't auto-fetch, only when explicitly triggered
		retry: 1,
	});

	// Show toast notification when commit query fails
	useEffect(() => {
		if (commitQuery.isError) {
			toast.error(t`Failed to process transaction. Please try again.`);
			trackError(analytics, commitQuery.error, {
				component: "ReviewPage",
				action: "commitTransaction",
			});
		}
	}, [commitQuery.isError, commitQuery.error, analytics]);

	const handleBack = () => {
		navigate(
			navigateWithRampsState(search, {
				to: "/ramps/identification",
				search: { step: "identification" },
			}),
		);
	};

	const handleEdit = (
		section:
			| "amount"
			| "name"
			| "phone"
			| "address"
			| "date-of-birth"
			| "identification"
			| "bank-account"
			| "deposit-location",
	) => {
		// Track edit action
		const editProps: FormAnalyticsProperties = {
			field_name: section,
			is_editing: true,
			form_step: "review",
		};
		trackEvent(ANALYTICS_EVENTS.FORM_FIELD_EDITED, editProps);

		// Set field as editable in session storage for ALL field types
		const transactionKey = search.transaction_id || "default";
		const sessionKey = `${section}-editable-${transactionKey}`;
		sessionStorage.setItem(sessionKey, "true");

		switch (section) {
			case "amount":
				// Navigate to appropriate page based on operation
				if (store.operation === "deposit") {
					navigate(
						navigateWithRampsState(search, {
							to: "/ramps/deposit",
							search: { step: "deposit", from_review: "true" },
						}),
					);
				} else {
					navigate(
						navigateWithRampsState(search, {
							to: "/ramps/calculator",
							search: { step: "calculator", from_review: "true" },
						}),
					);
				}
				break;
			case "deposit-location":
				setLocationSheetOpen(true);
				break;
			case "phone":
				navigate(
					navigateWithRampsState(search, {
						to: "/ramps/phone",
						search: { step: "phone" },
					}),
				);
				break;
			case "name":
				navigate(
					navigateWithRampsState(search, {
						to: "/ramps/name",
						search: { step: "name" },
					}),
				);
				break;
			case "address":
				navigate(
					navigateWithRampsState(search, {
						to: "/ramps/address",
						search: { step: "address" },
					}),
				);
				break;
			case "date-of-birth":
				navigate(
					navigateWithRampsState(search, {
						to: "/ramps/date-of-birth",
						search: { step: "date-of-birth" },
					}),
				);
				break;
			case "identification":
				navigate(
					navigateWithRampsState(search, {
						to: "/ramps/identification",
						search: { step: "identification" },
					}),
				);
				break;
			case "bank-account":
				// Clear selected bank account to allow new entry
				// This keeps the returning user flag but removes the saved account
				rampsActions.clearSelectedBankAccount();

				// Mark as editable for session tracking
				sessionStorage.setItem(
					`bank-account-editable-${transactionKey}`,
					"true",
				);

				navigate(
					navigateWithRampsState(search, {
						to: "/ramps/digital-offramp-info",
						search: { step: "digital-offramp-info" },
					}),
				);
				break;
		}
	};

	// Function to handle transaction directly
	const handlePickupCash = async () => {
		try {
			// Get transaction ID from URL or store as fallback
			const transactionId = search.transaction_id || store.transactionId;

			// Validate prerequisites before submitting
			if (!transactionId) {
				toast.error("Missing transaction ID");
				return;
			}

			// Auth token is already available from component scope (supports both v2 and v5 API)
			if (!authToken) {
				toast.error("Missing authentication token");
				return;
			}

			// Track transaction initiation
			const isDeposit = store.operation === "deposit";
			const transactionProps: TransactionAnalyticsProperties = {
				transaction_id: transactionId,
				amount: Number(search.amount || store.amount || 0),
				currency: store.assetCode || "USDC",
				source_currency: isDeposit
					? store.payoutCurrency || "USD"
					: store.assetCode || "USDC",
				destination_currency: isDeposit
					? store.assetCode || "USDC"
					: store.payoutCurrency || "USD",
				service_option: store.serviceOption,
				transaction_state: "initiated",
			};
			trackEvent(ANALYTICS_EVENTS.TRANSACTION_INITIATED, transactionProps);

			// Both deposit and withdraw flows call commit
			// Check if validation was completed
			if (!store.transactionResponse?.readyForCommit) {
				toast.error(
					"Validation not complete — please go through the full flow.",
				);
				return;
			}

			// Proceed with commit — the MoneyGram API returns wallet instructions
			// (accountAddress, operationMemo) in the commit RESPONSE, so we must
			// commit first, then tell the parent to send funds.
			const commitResult = await commitQuery.refetch();

			if (commitResult.isSuccess && commitResult.data) {
				// Store the commit response for the status page
				rampsActions.setCommitResponse(commitResult.data);

				// --- Post-Commit Interception for Embedded Mode ---
				// The commit response contains transactionCryptoNetworkInstructions
				// (accountAddress + operationMemo). If embedded, send this to the
				// parent so it can prompt the user's wallet to send funds on-chain.
				// We pause navigation until the parent confirms the send is done.
				if (
					store.apiVersion === "v5" &&
					isEmbedded() &&
					commitResult.data.transactionCryptoNetworkInstructions
				) {
					setIsWaitingForParent(true);

					const walletSendPayload = {
						transactionId: commitResult.data.transactionId,
						transactionCryptoNetworkInstructions:
							commitResult.data.transactionCryptoNetworkInstructions,
						referenceNumber: commitResult.data.referenceNumber,
						amount: search.amount || store.amount,
						assetCode: store.assetCode || "USDC",
					};

					sendToWebView("COMMIT_REQUEST", walletSendPayload);

					// Track: postMessage sent to parent wallet with tx, chain, and wallet context
					const embeddedProps: EmbeddedWalletEventProperties = {
						transaction_id: commitResult.data.transactionId,
						amount: search.amount || store.amount,
						asset_code: store.assetCode || "USDC",
						wallet_provider: store.walletProvider,
						message_type: "COMMIT_REQUEST",
						has_crypto_instructions: true,
						reference_number: commitResult.data.referenceNumber,
					};
					trackEvent(ANALYTICS_EVENTS.EMBEDDED_COMMIT_REQUESTED, embeddedProps);

					// Wait for parent to confirm wallet send (COMMIT_PROCEED) or report failure (COMMIT_ERROR)
					const result = await waitForCommitConfirmation();
					setIsWaitingForParent(false);

					// Unmount / user-initiated cancel — bail out, no analytics or navigation
					if (result.error === "Cancelled") return;

					if (result.success) {
						trackEvent(ANALYTICS_EVENTS.EMBEDDED_COMMIT_CONFIRMED, {
							...embeddedProps,
							message_type: "COMMIT_PROCEED",
						});
					} else {
						trackEvent(ANALYTICS_EVENTS.EMBEDDED_COMMIT_FAILED, {
							...embeddedProps,
							message_type: "COMMIT_ERROR",
							error_message: result.error,
						});
						if (result.error) {
							toast.error(result.error || t`Wallet send was not confirmed.`);
						}
						// Still navigate to status — the commit already happened
					}
				}

				rampsActions.setCurrentStep("status");

				// Build transaction payload for parent WebView (sep24 or v5 fallback)
				const transactionPayload = commitResult.data
					.sep24TransactionResponse ?? {
					id: commitResult.data.transactionId,
					status: commitResult.data.status ?? "unknown",
					more_info_url: "",
					stellar_transaction_id: "",
					external_transaction_id: commitResult.data.transactionId,
				};
				if (
					isFeatureEnabled(
						FEATURE_FLAGS.RAMPS_REVIEW_LEGACY_POSTMESSAGE_ENABLED,
					)
				) {
					const sent = sendLegacySep24CloseNotification(
						transactionPayload as Sep24TransactionResponse,
					);
					if (sent) {
						trackEvent(ANALYTICS_EVENTS.LEGACY_POSTMESSAGE_SENT, {
							...transactionProps,
							transaction_id: commitResult.data.transactionId,
						});
					}
				}
				if (
					isFeatureEnabled(FEATURE_FLAGS.RAMPS_REVIEW_POSTMESSAGE_ENABLED) &&
					isEmbedded()
				) {
					sendToWebView("COMMIT_RESULT", { transaction: transactionPayload });
					trackEvent(ANALYTICS_EVENTS.POSTMESSAGE_SENT, {
						...transactionProps,
						transaction_id: commitResult.data.transactionId,
					});
				}

				// Track completed Ramps UI flow (commit API success)
				trackEvent(ANALYTICS_EVENTS.RAMPS_UI_FLOW_COMPLETED, {
					...transactionProps,
					transaction_state: "committed",
				});

				// Update person property with confirmed transaction timestamp
				analytics.setUserProperties({
					last_transaction_at: new Date().toISOString(),
				});

				navigate(
					navigateWithRampsState(search, {
						to: "/ramps/status",
						search: {
							step: "status",
							transaction_id: commitResult.data.transactionId,
							token: authToken,
						},
					}),
				);
			} else {
				// Final commit failed - handle error appropriately
			}
		} catch (error) {
			trackError(analytics, error, {
				component: "ReviewPage",
				action: "handleConfirm",
			});
		}
	};

	// Function to handle terms drawer state
	const handleTermsDrawerChange = (open: boolean) => {
		if (open) {
			// Track when user opens terms drawer
			trackEvent(ANALYTICS_EVENTS.TERMS_VIEWED, {
				page_path: "/ramps/review",
			});
		}
		setShowTermsDrawer(open);
	};

	// Helper function to get full name from store
	const getFullName = () => {
		const { firstName, lastName, middleName, secondLastName } = store;
		const parts = [
			capitalizeFirstLetter(firstName),
			capitalizeFirstLetter(middleName),
			capitalizeFirstLetter(lastName),
			capitalizeFirstLetter(secondLastName),
		].filter(Boolean);
		return parts.join(" ");
	};

	// Check if this is a deposit flow
	const isDepositFlow = store.operation === "deposit";

	const getCommitLabel = () => {
		// Deposit flow - user is depositing cash
		if (isDepositFlow) {
			return t`Confirm deposit`;
		}
		// Withdraw flow
		if (store.serviceOption === "WILL_CALL") {
			return t`Pick up cash`;
		}
		return t`Confirm withdrawal`;
	};

	const getAccountLabel = () => {
		if (store.serviceOption === "BANK_DEPOSIT") {
			return t`Bank Account`;
		}
		return t`Account`;
	};

	// Get amount label based on flow type
	const getAmountLabel = () => {
		if (isDepositFlow) {
			return t`Deposit amount`;
		}
		return t`Withdraw amount`;
	};

	// Check if profile data comes from backend (existing user profile)
	const isExistingProfile = store.quoteResponse?.isExistingProfile ?? false;

	// Helper function to format date of birth for display
	const formatDateOfBirth = (dateString: string) => {
		if (!dateString) return "";

		// Handle both MM/DD/YYYY and YYYY-MM-DD formats
		if (dateString.includes("/")) {
			// Already in MM/DD/YYYY format
			return dateString;
		}
		if (dateString.includes("-")) {
			// Convert from YYYY-MM-DD to MM/DD/YYYY
			const [year, month, day] = dateString.split("-");
			return `${month}/${day}/${year}`;
		}

		return dateString;
	};

	// Helper function to mask ID number (show only last 4 digits)
	const maskIdNumber = (idNumber: string) => {
		if (!idNumber) return "";
		if (idNumber.length <= 4) return idNumber;

		// Show only last 4 digits, mask the rest with asterisks
		const lastFour = idNumber.slice(-4);
		const maskedPart = "*".repeat(idNumber.length - 4);
		return `${maskedPart}${lastFour}`;
	};

	// Helper function to mask bank account/code (show only last 4 digits)
	const maskBankField = (value: string) => {
		if (!value) return "";
		if (value.length <= 4) return value;

		// Show only last 4 digits, mask the rest with asterisks
		const lastFour = value.slice(-4);
		const maskedPart = "*".repeat(Math.min(value.length - 4, 8)); // Limit mask length to 8 asterisks
		return `${maskedPart}${lastFour}`;
	};

	// Helper function to extract and validate bank fields from stored bankData
	// Helper: Extract bank fields from GFFP data
	const extractBankFieldsFromData = useCallback(
		(bankData: Record<string, string>) => {
			// Extract all fields with 'targetAccount.' prefix and map to clean object
			const targetAccountFields: Record<string, string> = {};

			Object.entries(bankData).forEach(([key, value]) => {
				if (key.startsWith("targetAccount.")) {
					// Remove 'targetAccount.' prefix to get clean field name
					const cleanKey = key.replace("targetAccount.", "");
					targetAccountFields[cleanKey] = String(value);
				}
			});

			return targetAccountFields;
		},
		[],
	);

	// Helper: Save bank account to digital profile
	const saveBankAccountToProfile = useCallback(
		async (
			digitalProfileId: string,
			targetAccountProfileId: string,
			bankFieldsGFFP: typeof store.bankFieldsGFFP,
		) => {
			if (!bankFieldsGFFP) return;

			const bankData = bankFieldsGFFP.values as Record<string, string>;
			const extractedFields = extractBankFieldsFromData(bankData);

			if (extractedFields && Object.keys(extractedFields).length > 0) {
				try {
					await saveBankAccountMutation.mutateAsync({
						digitalProfileId,
						saveRequest: {
							targetAccountProfileId,
							targetAccount: extractedFields,
						},
					});
				} catch (_error) {
					// Non-blocking - continue flow even if save fails
				}
			}
		},
		[saveBankAccountMutation, extractBankFieldsFromData],
	);

	// Helper function to format bank account info (masked)
	const formatBankAccountInfo = (bankFields?: {
		schema: Array<{
			field: string;
			dataType: string;
			required: boolean;
			fieldLabel?: string;
		}>;
		values: Record<string, unknown>;
	}) => {
		if (!bankFields?.values || !bankFields?.schema) return null;

		// Helper to find field by checking if field name contains the search term (case-insensitive)
		const findFieldByPartialMatch = (searchTerms: string[]) => {
			return bankFields.schema.find(
				(field) =>
					searchTerms.some((term) =>
						field.field.toLowerCase().includes(term.toLowerCase()),
					) && bankFields.values[field.field],
			);
		};

		// Find fields with dot notation support
		const bankNameField = findFieldByPartialMatch([
			"bankname",
			"institutionname",
			"bank_name",
			"institution_name",
		]);
		const bankCodeField = findFieldByPartialMatch([
			"bankcode",
			"routingnumber",
			"swiftcode",
			"sortcode",
			"bank_code",
			"routing_number",
			"swift_code",
			"sort_code",
		]);
		const accountNumberField = findFieldByPartialMatch([
			"accountnumber",
			"account_number",
			"iban",
		]);

		// Check if we should display bank name from selectedBankChoice
		const shouldDisplaySelectedBankName =
			store.selectedBankChoice?.attrName === "bankName" &&
			store.selectedBankChoice?.choiceName;

		return (
			<div className="space-y-2">
				{/* Bank Name (if exists, unmasked) */}
				{shouldDisplaySelectedBankName && (
					<div>
						<RampsText variant="body-sm" color="secondary">
							{bankNameField?.fieldLabel || t`Bank Name`}
						</RampsText>
						<RampsText variant="body-default" color="primary">
							{shouldDisplaySelectedBankName
								? store.selectedBankChoice?.choiceName
								: ""}
						</RampsText>
					</div>
				)}

				{/* Bank Code (masked) */}
				{bankCodeField && (
					<div>
						<RampsText variant="body-sm" color="secondary">
							{bankCodeField.fieldLabel || t`Bank Code`}
						</RampsText>
						<div className="flex items-center gap-2">
							<RampsText variant="body-default" color="primary">
								{showBankCode
									? String(bankFields.values[bankCodeField.field])
									: maskBankField(
											String(bankFields.values[bankCodeField.field]),
										)}
							</RampsText>
							<button
								type="button"
								onClick={() => setShowBankCode(!showBankCode)}
								className="p-1 hover:bg-gray-100 rounded transition-colors"
								aria-label={
									showBankCode ? t`Hide bank code` : t`Show bank code`
								}
							>
								{showBankCode ? (
									<EyeOff className="w-4 h-4 text-primary-text" />
								) : (
									<Eye className="w-4 h-4 text-primary-text" />
								)}
							</button>
						</div>
					</div>
				)}

				{/* Account Number (masked) */}
				{accountNumberField && (
					<div>
						<RampsText variant="body-sm" color="secondary">
							{accountNumberField.fieldLabel || t`Account Number`}
						</RampsText>
						<div className="flex items-center gap-2">
							<RampsText variant="body-default" color="primary">
								{showAccountNumber
									? String(bankFields.values[accountNumberField.field])
									: maskBankField(
											String(bankFields.values[accountNumberField.field]),
										)}
							</RampsText>
							<button
								type="button"
								onClick={() => setShowAccountNumber(!showAccountNumber)}
								className="p-1 hover:bg-gray-100 rounded transition-colors"
								aria-label={
									showAccountNumber
										? t`Hide account number`
										: t`Show account number`
								}
							>
								{showAccountNumber ? (
									<EyeOff className="w-4 h-4 text-primary-text" />
								) : (
									<Eye className="w-4 h-4 text-primary-text" />
								)}
							</button>
						</div>
					</div>
				)}
			</div>
		);
	};

	// Simple state for revalidation
	const [isRevalidating, setIsRevalidating] = useState(false);
	const [hasRevalidated, setHasRevalidated] = useState(false);

	/**
	 * Check if data comes from a trusted source (API pre-filled)
	 */
	const isDataFromTrustedSource = useCallback((): boolean => {
		// Check if we have transaction_id and token (supports both v2 and v5 API)
		const tokenFromSearch = search.accesstoken || search.token;
		const hasApiTokens = !!(search.transaction_id && tokenFromSearch);

		// Check if we have complete ID data that would normally require validation
		const hasCompleteIdData = !!(
			store.idType &&
			store.idNumber &&
			store.idIssueCountry &&
			store.firstName &&
			store.lastName &&
			store.phone &&
			store.address
		);

		return hasApiTokens && hasCompleteIdData;
	}, [
		search.transaction_id,
		search.token,
		search.accesstoken,
		store.idType,
		store.idNumber,
		store.idIssueCountry,
		store.firstName,
		store.lastName,
		store.phone,
		store.address,
	]);

	// Function to handle revalidation when user edits any field
	const handleRevalidation = useCallback(async () => {
		if (!store.transactionId) {
			toast.error(t`Missing transaction data. Please restart the process.`);
			setIsRevalidating(false);
			return;
		}

		setIsRevalidating(true);

		try {
			// Rebuild the entire payload with the latest store data
			// This ensures any edited field (address, phone, name, etc.) is included
			const updatedPayload = buildSendCommitPayload(store);

			// Validate transaction with updated amount
			// Determine operation: use store.operation if set, otherwise infer from cashInLocation
			const operation =
				store.operation || (store.cashInLocation ? "deposit" : "withdraw");
			const validationResponse = await apiClient.sendCommit.validateTransaction(
				store.transactionId,
				updatedPayload,
				{ params: { operation } },
			);

			// Check for errors in the response and display them
			if (validationResponse?.errors && validationResponse.errors.length > 0) {
				const error = validationResponse.errors[0];

				// Store validation response BEFORE navigating (so additional-info page has the fields)
				rampsActions.setTransactionResponse(validationResponse);

				// Check if it's an offendingFields error that requires additional info
				if (error.offendingFields) {
					// Don't show toast for offendingFields errors - they're handled by navigating to additional-info page
					rampsActions.setCurrentStep("additional-info");
					navigate(
						navigateWithRampsState(search, {
							to: "/ramps/additional-info",
							search: { step: "additional-info" },
						}),
					);
				} else {
					// Only show toast for errors without offendingFields
					const errorMessage =
						error.message ||
						t`Validation failed. Please check your information.`;
					toast.error(errorMessage, { header: t`Validation Error` });
				}

				setIsRevalidating(false);
				return;
			}

			// Store updated validation response and payload
			rampsActions.setTransactionResponse(validationResponse);
			rampsActions.setValidatePayload(updatedPayload);

			// If this is a user with a profile and new bank account details, save to profile
			// Do not save DIRECT_TO_ACCT options as bank accounts
			if (
				store.digitalProfileId &&
				store.bankFieldsGFFP &&
				validationResponse.targetAccountProfileId &&
				store.serviceOption !== "DIRECT_TO_ACCT"
			) {
				await saveBankAccountToProfile(
					store.digitalProfileId,
					validationResponse.targetAccountProfileId,
					store.bankFieldsGFFP,
				);
			}
		} catch (error) {
			captureException(error, {
				tags: { component: "ReviewPage", action: "revalidateAndNavigate" },
			});
			toast.error(t`Failed to update transaction. Please try again.`);
		} finally {
			setIsRevalidating(false);
		}
	}, [store, apiClient.sendCommit, navigate, search, saveBankAccountToProfile]);

	// Function to perform initial validation for trusted data
	const performInitialValidation = useCallback(async () => {
		if (!store.transactionId || !authToken) {
			toast.error(
				t`Missing transaction information. Please restart the process.`,
			);
			return;
		}

		setIsRevalidating(true);

		try {
			// Build validation payload using the proper builder function
			const payload = buildSendCommitPayload(store);

			// Store the payload for future use
			rampsActions.setValidatePayload(payload);

			// Validate transaction with API
			// Determine operation: use store.operation if set, otherwise infer from cashInLocation
			const operation =
				store.operation || (store.cashInLocation ? "deposit" : "withdraw");
			const validationResponse = await apiClient.sendCommit.validateTransaction(
				store.transactionId,
				payload,
				{ params: { operation } },
			);

			// Check for errors in the response and display them
			if (validationResponse?.errors && validationResponse.errors.length > 0) {
				const error = validationResponse.errors[0];

				// Store validation response BEFORE navigating (so additional-info page has the fields)
				rampsActions.setTransactionResponse(validationResponse);

				// Check if it's an offendingFields error that requires additional info
				if (error.offendingFields) {
					// Don't show toast for offendingFields errors - they're handled by navigating to additional-info page
					rampsActions.setCurrentStep("additional-info");
					navigate(
						navigateWithRampsState(search, {
							to: "/ramps/additional-info",
							search: { step: "additional-info" },
						}),
					);
				} else {
					// Only show toast for errors without offendingFields
					const errorMessage =
						error.message ||
						t`Validation failed. Please check your information.`;
					toast.error(errorMessage, { header: t`Validation Error` });
				}

				setIsRevalidating(false);
				return;
			}

			// Store validation response
			rampsActions.setTransactionResponse(validationResponse);

			// If this is a user with a profile and new bank account details, save to profile
			// Do not save DIRECT_TO_ACCT options as bank accounts
			if (
				store.digitalProfileId &&
				store.bankFieldsGFFP &&
				validationResponse.targetAccountProfileId &&
				store.serviceOption !== "DIRECT_TO_ACCT"
			) {
				await saveBankAccountToProfile(
					store.digitalProfileId,
					validationResponse.targetAccountProfileId,
					store.bankFieldsGFFP,
				);
			}
		} catch (error) {
			const errorMessage =
				(error as Error & { response?: { data?: { message?: string } } })
					?.response?.data?.message ||
				(error as Error)?.message ||
				t`Validation failed. Please check your information and try again.`;
			toast.error(errorMessage);
		} finally {
			setIsRevalidating(false);
		}
	}, [
		store.transactionId,
		authToken,
		store,
		apiClient.sendCommit,
		store.digitalProfileId,
		store.bankFieldsGFFP,
		navigate,
		search,
		saveBankAccountToProfile,
	]);

	// When user changes deposit location from review: FeeQuote then SendValidate
	const handleLocationChange = useCallback(
		async (_selectedLocation: import("@/api").PickupLocation) => {
			setIsLocationUpdating(true);
			try {
				// Use latest store state (Locator has already called setCashInLocation)
				const currentStore = rampsStore.state;
				const inputAmount = search.amount || currentStore.amount || "0";
				const quoteRequest = buildDepositQuoteRequest({
					cashInLocation: currentStore.cashInLocation ?? undefined,
					payoutCountry: currentStore.payoutCountry,
					state: currentStore.state,
					countries: currentStore.countries,
					inputAmount,
					sendCurrencyCode: currentStore.cashInSendCurrency,
				});

				if (!quoteRequest) {
					toast.error(t`Missing location or amount. Please try again.`);
					setLocationSheetOpen(false);
					return;
				}

				// 1. FeeQuote with new location
				let quoteResponse: QuoteResponse | undefined;
				try {
					quoteResponse = await apiClient.quote.getQuote(quoteRequest, {
						params: { operation: "deposit" },
					});
				} catch (error) {
					captureException(error, {
						tags: {
							component: "ReviewPage",
							action: "handleLocationChange_FeeQuote",
						},
					});
					rampsActions.clearQuoteResponse();
					rampsActions.clearValidationData();
					rampsActions.clearCashInLocation();
					toast.error(t`Failed to update location. Please try again.`);
					return;
				}

				if (!quoteResponse) return;

				rampsActions.setQuoteResponse(quoteResponse);
				if (quoteResponse.senderProfile) {
					rampsActions.setSenderProfile(quoteResponse.senderProfile);
				}

				// 2. SendValidate with new payload
				const payload = buildSendCommitPayload(rampsStore.state);
				const transactionId =
					quoteResponse.transactions?.[0]?.transactionId ??
					rampsStore.state.transactionId;
				if (!transactionId) {
					toast.error(t`Missing transaction. Please try again.`);
					setLocationSheetOpen(false);
					return;
				}

				try {
					const validationResponse =
						await apiClient.sendCommit.validateTransaction(
							transactionId,
							payload,
							{ params: { operation: "deposit" } },
						);

					if (
						validationResponse?.errors &&
						validationResponse.errors.length > 0
					) {
						const error = validationResponse.errors[0];
						rampsActions.setTransactionResponse(validationResponse);
						if (error.offendingFields) {
							rampsActions.setCurrentStep("additional-info");
							navigate(
								navigateWithRampsState(search, {
									to: "/ramps/additional-info",
									search: { step: "additional-info" },
								}),
							);
						} else {
							const errorMessage =
								error.message ||
								t`Validation failed. Please check your information.`;
							toast.error(errorMessage, {
								header: t`Validation Error`,
							});
						}
					} else {
						rampsActions.setTransactionResponse(validationResponse);
						rampsActions.setValidatePayload(payload);
					}
				} catch (error) {
					captureException(error, {
						tags: {
							component: "ReviewPage",
							action: "handleLocationChange_SendValidate",
						},
					});
					rampsActions.clearValidationData();
					toast.error(t`Failed to validate transaction. Please try again.`);
				}
			} finally {
				setIsLocationUpdating(false);
				setLocationSheetOpen(false);
			}
		},
		[apiClient.quote, apiClient.sendCommit, navigate, search],
	);

	// Redirect if validation not completed and not from trusted source
	const shouldRedirect =
		!store.transactionResponse && !isDataFromTrustedSource();

	React.useEffect(() => {
		if (shouldRedirect) {
			rampsActions.setCurrentStep("identification");
			navigate(
				navigateWithRampsState(search, {
					to: "/ramps/identification",
					search: { step: "identification" },
				}),
			);
		}
	}, [shouldRedirect, navigate, search]);

	// Simple one-time check for revalidation when component mounts
	React.useEffect(() => {
		// Check if user explicitly came from editing any field by checking session storage
		const transactionKey = search.transaction_id || "default";

		const editableFields = [
			"amount",
			"name",
			"phone",
			"address",
			"date-of-birth",
			"identification",
			"bank-account",
			"deposit-location",
		];

		// Check if any field was marked as edited
		const editedField = editableFields.find(
			(field) =>
				sessionStorage.getItem(`${field}-editable-${transactionKey}`) ===
				"true",
		);

		// Scenario 1: Revalidation after editing (existing logic)
		// Check for transactionId instead of transactionResponse because we clear
		// transactionResponse when getting a new quote (new transactionId)
		if (
			store.transactionId &&
			editedField && // Only if explicitly set from edit button
			!hasRevalidated &&
			!isRevalidating
		) {
			// Clear ALL session storage flags since we're handling the revalidation
			editableFields.forEach((field) => {
				sessionStorage.removeItem(`${field}-editable-${transactionKey}`);
			});

			setHasRevalidated(true); // Prevent future revalidations
			handleRevalidation();
		}
		// Scenario 2: Initial validation for trusted data (new logic)
		else if (
			!store.transactionResponse &&
			isDataFromTrustedSource() &&
			!hasRevalidated &&
			!isRevalidating
		) {
			setHasRevalidated(true); // Prevent future validations
			performInitialValidation();
		}
	}, [
		handleRevalidation,
		hasRevalidated,
		isDataFromTrustedSource,
		isRevalidating,
		performInitialValidation,
		search.transaction_id,
		store.transactionId,
		store.transactionResponse,
	]);

	// Clean up commit wait on unmount
	useEffect(() => {
		return () => {
			cancelWait();
		};
	}, [cancelWait]);

	// Show loading while redirecting or revalidating
	if (shouldRedirect || isRevalidating) {
		return <LoadingSpinner size="lg" />;
	}

	return (
		<div className="flex flex-col h-dvh bg-ramps-background-base relative">
			{/* Header */}
			<div className="flex items-center justify-between p-4 shrink-0">
				<button
					type="button"
					onClick={handleBack}
					className="w-8 h-8 flex items-center justify-center hover:opacity-80 transition-opacity"
					aria-label={t`Back`}
				>
					<ArrowLeft className="w-6 h-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="px-4 mb-8 shrink-0">
				<Progress
					value={100}
					className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
				/>
			</div>

			{/* Main Content Area - Scrollable */}
			<div className="flex-1 overflow-y-auto pb-80">
				<div className="px-4">
					<div className="text-left mb-8">
						<RampsText variant="title-lg-bold" color="primary" className="mb-2">
							{t`Review`}
						</RampsText>
					</div>

					{/* Review Items */}
					<div className="space-y-4">
						{/* Amount - dynamic label based on flow */}
						<div className="flex flex-row p-1 justify-between">
							<div>
								<div className="flex justify-between items-center">
									<RampsText variant="body-sm" color="secondary">
										{getAmountLabel()}
									</RampsText>
								</div>
								<div className="flex flex-row items-baseline gap-1">
									<RampsText variant="title-lg-bold" color="primary">
										{(() => {
											// Cash-in flow: use payoutAmountDetails, Cash-out flow: use sendAmountDetails
											const currentAmount = isDepositFlow
												? store.transactionResponse?.payoutAmountDetails?.totalAmount?.value?.toString() ||
													store.amount
												: store.transactionResponse?.sendAmountDetails?.totalAmount?.value?.toString() ||
													store.amount;
											return currentAmount
												? formatNumber(Number.parseFloat(currentAmount))
												: "";
										})()}
									</RampsText>
									<RampsText variant="body-sm" color="secondary">
										USDC
									</RampsText>
								</div>

								{/* COMMENTED OUT: Exchange currency display section */}
								{/* eslint-disable-next-line @typescript-eslint/no-unused-expressions */}
								{false && (
									<>
										<RampsText variant="body-sm" color="secondary">
											{t`Exchange currency`}
										</RampsText>
										<div className="flex flex-row items-baseline gap-1">
											<RampsText variant="title-lg-bold" color="primary">
												{(() => {
													// Show total converted amount before fees (like calculator page)
													const currentAmount =
														search.amount ||
														store.amount ||
														store.transactionResponse?.sendAmountDetails?.totalAmount?.value?.toString();
													const exchangeRate =
														store.transactionResponse?.payoutAmountDetails
															?.fxRate;

													if (currentAmount && exchangeRate) {
														const usdcAmount = Number.parseFloat(
															currentAmount || "0",
														);
														const convertedAmount =
															usdcAmount * Number(exchangeRate);
														return formatNumber(convertedAmount);
													}

													// Fallback to payout amount if available
													return store.transactionResponse?.payoutAmountDetails
														?.totalAmount?.value
														? formatNumber(
																store.transactionResponse?.payoutAmountDetails
																	?.totalAmount?.value || 0,
															)
														: search.amount || store.amount
															? formatNumber(
																	Number.parseFloat(
																		search.amount || store.amount || "0",
																	),
																)
															: "";
												})()}
											</RampsText>
											<RampsText variant="body-default" color="secondary">
												{store.transactionResponse?.payoutAmountDetails
													?.totalAmount?.currencyCode || "USD"}
											</RampsText>
										</div>
									</>
								)}
							</div>
							{/* Show edit button based on amount editability logic */}
							{isAmountEditable(search.amount) && (
								<div className="flex flex-row items-center gap-1">
									<button
										type="button"
										onClick={() => handleEdit("amount")}
										className="px-3 py-1 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
									>
										<RampsText variant="body-sm" color="primary">
											{t`Edit`}
										</RampsText>
									</button>
								</div>
							)}
						</div>

						{/* Deposit Location - Only shown for deposit flow */}
						{isDepositFlow && (
							<div className="flex flex-row p-1 justify-between">
								<div className="flex-1">
									<div className="flex justify-between items-center mb-1">
										<RampsText variant="body-sm" color="secondary">
											{t`Deposit location`}
										</RampsText>
									</div>
									<div>
										{store.cashInLocation ? (
											<>
												<RampsText variant="body-bold" color="primary">
													{store.cashInLocation.name}
												</RampsText>
												<RampsText variant="body-default" color="primary">
													{store.cashInLocation.address}
												</RampsText>
												<RampsText variant="body-default" color="primary">
													{store.cashInLocation.city},{" "}
													{store.cashInLocation.state}
												</RampsText>
											</>
										) : (
											<RampsText variant="body-default" color="error">
												{t`No location selected`}
											</RampsText>
										)}
									</div>
								</div>
								<div className="flex flex-row items-center gap-1">
									<button
										type="button"
										onClick={() => handleEdit("deposit-location")}
										className="px-3 py-1 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
									>
										<RampsText variant="body-sm" color="primary">
											{t`Edit`}
										</RampsText>
									</button>
								</div>
							</div>
						)}

						{/* Full name */}
						<EditableField
							label={t`Full name`}
							value={getFullName()}
							onEdit={() => handleEdit("name")}
							disabled={isExistingProfile}
						/>

						{/* Phone number */}
						<EditableField
							label={t`Phone number`}
							value={`${store.phoneCountryCode} ${store.phone}`}
							onEdit={() => handleEdit("phone")}
						/>

						{/* Date of birth */}
						<EditableField
							label={t`Date of birth`}
							value={formatDateOfBirth(store.dateOfBirth)}
							onEdit={() => handleEdit("date-of-birth")}
							disabled={isExistingProfile}
						/>

						{/* Address */}
						<EditableField
							label={t`Address`}
							value={
								<div>
									<RampsText variant="body-bold" color="primary">
										{store.address}
									</RampsText>
									{store.address2 && (
										<RampsText variant="body-default" color="primary">
											{store.address2}
										</RampsText>
									)}
									<RampsText variant="body-default" color="primary">
										{store.city}, {formatStateForDisplay(store.addressState)}{" "}
										{store.zipCode}
									</RampsText>
									<RampsText variant="body-default" color="primary">
										{getCountryLabel(store.addressCountry)}
									</RampsText>
								</div>
							}
							onEdit={() => handleEdit("address")}
						/>

						{/* Identification */}
						<div className="flex flex-row p-1 justify-between">
							<div className="flex-1">
								<div className="flex justify-between items-center mb-1">
									<RampsText variant="body-sm" color="secondary">
										{t`Identification`}
									</RampsText>
								</div>
								<div>
									<RampsText variant="body-bold" color="primary">
										{getIdLabel(store.idType)}
									</RampsText>
									<div className="flex items-center gap-2">
										<RampsText variant="body-default" color="primary">
											{showIdNumber
												? store.idNumber
												: maskIdNumber(store.idNumber)}
										</RampsText>
										<button
											type="button"
											onClick={() => setShowIdNumber(!showIdNumber)}
											className="p-1 hover:bg-gray-100 rounded transition-colors"
											aria-label={
												showIdNumber ? t`Hide ID number` : t`Show ID number`
											}
										>
											{showIdNumber ? (
												<EyeOff className="w-4 h-4 text-primary-text" />
											) : (
												<Eye className="w-4 h-4 text-primary-text" />
											)}
										</button>
									</div>
									<RampsText variant="body-default" color="primary">
										{/* Only show state if ID type is NOT Passport or Government ID */}
										{store.idIssueState &&
										store.idType !== "PAS" &&
										store.idType !== "GOV"
											? `${formatStateForDisplay(store.idIssueState)}, `
											: ""}
										{getCountryLabel(store.idIssueCountry)}
									</RampsText>
								</div>
							</div>
							<div className="flex flex-row items-center gap-1">
								<button
									type="button"
									onClick={() => handleEdit("identification")}
									className="px-3 py-1 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
								>
									<RampsText variant="body-sm" color="primary">
										{t`Edit`}
									</RampsText>
								</button>
							</div>
						</div>

						{/* Bank Account Information - Only show for bank services (not deposit flow) */}
						{!isDepositFlow && isBankService(store.serviceOption) && (
							<>
								{/* Show saved bank account for users with profile */}
								{store.digitalProfileId && store.selectedBankAccount ? (
									<EditableField
										label={getAccountLabel()}
										value={
											<div>
												<RampsText variant="body-default" color="primary">
													{store.selectedBankAccount.accountNumberMasked}
												</RampsText>
											</div>
										}
										onEdit={() => handleEdit("bank-account")}
									/>
								) : (
									/* Show manual bank entry fields for new users */
									store.bankFieldsGFFP && (
										<EditableField
											label={getAccountLabel()}
											value={formatBankAccountInfo(store.bankFieldsGFFP)}
											onEdit={() => handleEdit("bank-account")}
										/>
									)
								)}
							</>
						)}
					</div>
				</div>
				{/* Reminder text - for bank services (withdraw) */}
				{!isDepositFlow && isBankService(store.serviceOption) && (
					<div className="p-4">
						<FormReminder
							header={t`Important Notice`}
							text={t`Funds will be deducted from your wallet immediately. This transaction cannot be reversed once confirmed. Please verify all details are correct.`}
							icon={TriangleAlert}
						/>
					</div>
				)}
			</div>

			{/* Fees Summary - Fixed to Bottom */}
			<FixedBottomBar className="bg-gradient-ramps-status-bl rounded-t-2xl p-6 shadow-2xl">
				<div className="flex justify-between items-center mb-4">
					<RampsText variant="body-default" color="primary">
						{t`Exchange rate`}
					</RampsText>
					<RampsText variant="body-default" color="primary">
						{(() => {
							// For deposit flows, use sendAmountDetails.fxRate
							// For withdraw flows, use payoutAmountDetails.fxRate
							const fxRate = isDepositFlow
								? store.transactionResponse?.sendAmountDetails?.fxRate
								: store.transactionResponse?.payoutAmountDetails?.fxRate;
							// For deposit flows, use sendAmountDetails.totalAmount.currencyCode (local currency user pays)
							// For withdraw flows, use payoutAmountDetails.totalAmount.currencyCode (local currency user receives)
							const currencyCode = isDepositFlow
								? store.transactionResponse?.sendAmountDetails?.totalAmount
										?.currencyCode ||
									store.payoutCurrency ||
									"USD"
								: store.transactionResponse?.payoutAmountDetails?.totalAmount
										?.currencyCode ||
									store.payoutCurrency ||
									"USD";
							if (fxRate) {
								return `1 ${store.assetCode || "USDC"} = ${formatNumber(Number(fxRate), { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ${currencyCode}`;
							}
							return `1 USDC = 1.0000 ${currencyCode}`;
						})()}
					</RampsText>
				</div>

				<div className="flex justify-between items-center mb-4">
					<RampsText variant="body-default" color="primary">
						{t`Fees`}
					</RampsText>
					<RampsText variant="body-default" color="primary">
						{(() => {
							// For deposit flows, use payoutAmountDetails.partnerFees * sendAmountDetails.fxRate
							// For withdraw flows, use payoutAmountDetails for currency
							const localCurrency = isDepositFlow
								? store.transactionResponse?.sendAmountDetails?.totalAmount
										?.currencyCode ||
									store.transactionResponse?.sendAmountDetails?.fees
										?.currencyCode ||
									store.payoutCurrency ||
									"USD"
								: store.transactionResponse?.payoutAmountDetails?.totalAmount
										?.currencyCode ||
									store.payoutCurrency ||
									"USD";

							if (isDepositFlow) {
								// For deposit flows: payoutAmountDetails.partnerFees.value * sendAmountDetails.fxRate
								const payoutPartnerFees = (
									store.transactionResponse?.payoutAmountDetails as
										| { partnerFees?: { value: number; currencyCode: string } }
										| undefined
								)?.partnerFees;
								const sendFxRate =
									store.transactionResponse?.sendAmountDetails?.fxRate;
								if (payoutPartnerFees && sendFxRate) {
									const feeInLocalCurrency =
										payoutPartnerFees.value * Number(sendFxRate);
									return `${formatNumber(feeInLocalCurrency)} ${localCurrency}`;
								}
							} else {
								// For withdraw flows: use existing logic
								const partnerFees =
									store.transactionResponse?.sendAmountDetails?.partnerFees;
								const exchangeRate =
									store.transactionResponse?.payoutAmountDetails?.fxRate;
								if (partnerFees && exchangeRate) {
									const feeInUSDC = partnerFees.value;
									const feeInLocalCurrency = feeInUSDC * Number(exchangeRate);
									return `${formatNumber(feeInLocalCurrency)} ${localCurrency}`;
								}
							}
							return `${formatNumber(0)} ${localCurrency}`;
						})()}
					</RampsText>
				</div>

				{/* Taxes - Only show when taxes value > 0 */}
				{(() => {
					if (isDepositFlow) {
						// Cash-in taxes are charged at the location in local fiat
						// (sendAmountDetails side); they add to "Amount to pay".
						const sendDetails = store.transactionResponse?.sendAmountDetails;
						const taxes = resolveCashInTaxes({
							sendTaxes: sendDetails?.taxes,
							payoutTaxes:
								store.transactionResponse?.payoutAmountDetails?.taxes,
							fxRate: Number(sendDetails?.fxRate ?? 1),
							depositCurrency:
								sendDetails?.totalAmount?.currencyCode ||
								store.payoutCurrency ||
								"USD",
						});
						if (!taxes) return null;
						return (
							<div className="flex justify-between items-center mb-4">
								<RampsText variant="body-default" color="primary">
									{t`Taxes`}
								</RampsText>
								<RampsText variant="body-default" color="primary">
									{`${formatNumber(taxes.value)} ${taxes.currencyCode}`}
								</RampsText>
							</div>
						);
					}
					const taxes = store.transactionResponse?.payoutAmountDetails?.taxes;
					const taxesValue = taxes?.value || 0;
					if (taxesValue > 0 && taxes) {
						return (
							<div className="flex justify-between items-center mb-4">
								<RampsText variant="body-default" color="primary">
									{t`Taxes`}
								</RampsText>
								<RampsText variant="body-default" color="primary">
									{`-${formatNumber(taxesValue)} ${taxes.currencyCode}`}
								</RampsText>
							</div>
						);
					}
					return null;
				})()}

				<div className="flex justify-between items-center mb-6">
					<RampsText variant="body-bold" color="primary">
						{isDepositFlow ? t`Amount to pay` : t`You Get`}
					</RampsText>
					<RampsText variant="body-bold" color="primary">
						{(() => {
							// For deposit flows, use sendAmountDetails.totalAmount (what user pays in local currency)
							// For withdraw flows, use payoutAmountDetails.totalAmount (what user receives)
							if (isDepositFlow) {
								const sendDetails =
									store.transactionResponse?.sendAmountDetails;
								if (sendDetails?.totalAmount?.value) {
									const currencyCode =
										sendDetails.totalAmount.currencyCode ||
										store.payoutCurrency ||
										"USD";
									return `${formatNumber(sendDetails.totalAmount.value)} ${currencyCode}`;
								}
								// Fallback
								const currentAmount = search.amount || store.amount || "0";
								const fallbackCurrency = store.payoutCurrency || "USD";
								return `${formatNumber(Number.parseFloat(currentAmount))} ${fallbackCurrency}`;
							} else {
								const payoutDetails =
									store.transactionResponse?.payoutAmountDetails;
								if (payoutDetails?.totalAmount?.value) {
									const currencyCode =
										payoutDetails.totalAmount.currencyCode ||
										store.payoutCurrency ||
										"USD";
									return `${formatNumber(payoutDetails.totalAmount.value)} ${currencyCode}`;
								}
								// Fallback
								const currentAmount = search.amount || store.amount || "0";
								const fallbackCurrency = store.payoutCurrency || "USD";
								return `${formatNumber(Number.parseFloat(currentAmount))} ${fallbackCurrency}`;
							}
						})()}
					</RampsText>
				</div>

				{/* Terms notice */}
				<div className="mb-4">
					<RampsText
						variant="body-sm"
						color="secondary"
						className="text-center"
					>
						{t`By continuing, you accept our`}{" "}
						<button
							type="button"
							onClick={() => setShowTermsDrawer(true)}
							className="font-medium hover:underline text-ramps-text-secondary underline bg-transparent border-none p-0 cursor-pointer"
						>
							{t`Terms of Use`}
						</button>{" "}
						{t`and`}{" "}
						<button
							type="button"
							onClick={() => setShowTermsDrawer(true)}
							className="font-medium hover:underline text-ramps-text-secondary underline bg-transparent border-none p-0 cursor-pointer"
						>
							{t`Privacy Policy`}
						</button>
						.
					</RampsText>
				</div>

				{/* Commit Button */}
				<RampsButton
					onClick={handlePickupCash}
					disabled={
						commitQuery.isFetching ||
						isWaitingForParent ||
						!store.transactionResponse?.readyForCommit
					}
					loading={commitQuery.isFetching || isWaitingForParent}
					className="w-full h-12 rounded-lg font-medium bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isWaitingForParent
						? t`Sending funds to wallet...`
						: getCommitLabel()}
				</RampsButton>
			</FixedBottomBar>

			{/* Terms and Conditions Drawer */}
			<Drawer open={showTermsDrawer} onOpenChange={handleTermsDrawerChange}>
				<DrawerContent className="bg-white border-ramps-border-default max-h-[80vh]">
					<DrawerTitle className="sr-only">{t`Terms and Conditions`}</DrawerTitle>
					<div className="px-6 pb-8 pt-2 overflow-y-auto">
						{/* Close button */}
						<DrawerClose asChild>
							<button
								type="button"
								className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center hover:opacity-80 transition-opacity"
							>
								<X className="w-5 h-5 text-gray-600" />
							</button>
						</DrawerClose>

						{/* Terms Content */}
						<div className="mt-4">
							<RampsText
								variant="title-lg-bold"
								color="primary"
								className="mb-6 text-left"
							>
								{t`Terms of use`}
							</RampsText>

							<div className="space-y-6 text-left">
								<div>
									<RampsText
										variant="body-default"
										color="primary"
										className="leading-relaxed"
									>
										{t`Circle Internet Financial, Inc. ("Circle") is responsible for providing the exchange service from USDC to USD. I agree to Circle's`}{" "}
										<a
											href="https://www.circle.com/legal/usdc-terms"
											target="_blank"
											rel="noopener noreferrer"
											className="font-medium hover:underline"
										>
											{t`USDC User Agreement`}
										</a>
										,{" "}
										<a
											href="https://www.circle.com/legal/acceptable-use-policy"
											target="_blank"
											rel="noopener noreferrer"
											className="font-medium hover:underline"
										>
											{t`Acceptable Use Policy`}
										</a>{" "}
										{t`and`}{" "}
										<a
											href="https://www.circle.com/legal/privacy-policy"
											target="_blank"
											rel="noopener noreferrer"
											className="font-medium hover:underline"
										>
											{t`Privacy Policy`}
										</a>
										.{" "}
										{t`I confirm I am the sole beneficial owner of the wallet and digital assets contained therein, and am acting solely on my own behalf.`}
									</RampsText>
								</div>

								<div>
									<RampsText
										variant="body-default"
										color="primary"
										className="leading-relaxed"
									>
										{t`MoneyGram Payment Systems, Inc. ("MoneyGram") is responsible for providing the money transfer service from USD to USD (for USD Cash Out) and from USD to any other applicable currency (for non-USD Cash Out). MoneyGram may apply a currency exchange for any non-USD Cash Out. MoneyGram makes money from currency exchange. The amounts shown are only estimates based on variable exchange rates. Final fees have not been applied.`}
									</RampsText>
								</div>

								<div>
									<RampsText
										variant="body-default"
										color="primary"
										className="leading-relaxed"
									>
										{t`I agree to MoneyGram's`}{" "}
										<a
											href="https://www.moneygram.com/us/en/help-center/crypto-terms-and-conditions"
											target="_blank"
											rel="noopener noreferrer"
											className="font-medium hover:underline"
										>
											{t`Terms and Conditions`}
										</a>
										.
										{t`By continuing with this transaction I consent to the collection, use, disclosure and transfer (including cross-border transfer) of my personal information as described in MoneyGram's`}{" "}
										<a
											href="https://www.moneygram.com/intl/Documents/V2-Documents/MPSI-US-Privacy-Notice-EN-Sept-2020.pdf"
											target="_blank"
											rel="noopener noreferrer"
											className="font-medium hover:underline"
										>
											{t`Privacy Notice`}
										</a>
										.
									</RampsText>
								</div>
							</div>
						</div>
					</div>
				</DrawerContent>
			</Drawer>

			{/* Deposit location Locator bottom sheet (cash-in flow) */}
			<Drawer open={locationSheetOpen} onOpenChange={setLocationSheetOpen}>
				<DrawerContent className="flex h-[85vh] max-h-[90vh] flex-col">
					<DrawerTitle className="sr-only">
						{t`Change deposit location`}
					</DrawerTitle>
					<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
						<Locator
							onContinue={handleLocationChange}
							isSubmitting={isLocationUpdating}
							isFromReview={true}
							title={t`Pick a location`}
							className="min-h-0"
						/>
					</div>
				</DrawerContent>
			</Drawer>
		</div>
	);
}
